import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  Paper,
  Text,
  Loader,
  Stack,
  Alert,
  Group,
  NumberFormatter,
  Badge,
  ActionIcon,
  Title,
  MantineColor,
} from '@mantine/core';
import { IconRefresh, IconAlertCircle } from '@tabler/icons-react';
import { httpsCallable } from 'firebase/functions';
import { useFunctions } from '../helpers/firebaseContext';
import { PortfolioPositionWithCustodianAccountsResponse, PortfolioPositionWithCustodianAccountsRow, GenevaRequest } from '../types';

interface PortfolioPositionTableProps {
  portfolioCode?: string;
  date?: string;
  showTitle?: boolean;
  onRowClick?: (investment: string, custodianAccount: string) => void;
  highlightInvestment?: string;
  highlightLocationAccount?: string;
  transactionQuantity?: number;
  onPositionsLoaded?: (positions: PortfolioPositionWithCustodianAccountsRow[]) => void;
  onLoadingChange?: (loading: boolean) => void;
  enableQuantityWarning?: boolean;
}

export const PortfolioPositionTable: React.FC<PortfolioPositionTableProps> = ({
  portfolioCode,
  date,
  showTitle = true,
  onRowClick,
  highlightInvestment,
  highlightLocationAccount,
  transactionQuantity,
  onPositionsLoaded,
  onLoadingChange,
  enableQuantityWarning = false,
}) => {
  const [positions, setPositions] = useState<PortfolioPositionWithCustodianAccountsRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const functions = useFunctions();
  functions.region = "europe-west1";

  const fetchPositions = async () => {
    if (!portfolioCode) {
      setPositions([]);
      onPositionsLoaded?.([]);
      setError(null);
      setLoading(false);
      onLoadingChange?.(false);
      return;
    }

    setLoading(true);
    onLoadingChange?.(true);
    setError(null);

    try {
      const getPortfolioPosition = httpsCallable<GenevaRequest, PortfolioPositionWithCustodianAccountsResponse>(
        functions,
        'getPortfolioPositionWithCustodianAccounts'
      );

      const requestData: GenevaRequest = { portfolioCode };
      if (date) {
        requestData.date = date;
        requestData.custom_date = true;
        console.log(`[PortfolioPositionTable] Fetching positions for ${portfolioCode} as of ${date}`);
      } else {
        console.log(`[PortfolioPositionTable] Fetching current positions for ${portfolioCode}`);
      }

      const result = await getPortfolioPosition(requestData);
      console.log(`[PortfolioPositionTable] Received ${result.data.rows?.length || 0} positions`);
      const rows = result.data.rows || [];
      setPositions(rows);
      onPositionsLoaded?.(rows);
    } catch (err) {
      console.error('Error fetching portfolio positions:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch portfolio positions');
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  };

  useEffect(() => {
    fetchPositions();
  }, [portfolioCode, date]);

  const handleRefresh = () => {
    fetchPositions();
  };

  // Generate a color based on investment type using hash
  const getInvestmentTypeColor = (typeCode: string | undefined): MantineColor => {
    if (!typeCode) return 'gray';

    // Generate consistent color based on hash
    const colors: MantineColor[] = ['red', 'grape', 'violet', 'blue', 'teal', 'green', 'orange'];
    let hash = 0;
    for (let i = 0; i < typeCode.length; i++) {
      hash = ((hash << 5) - hash) + typeCode.charCodeAt(i);
      hash = hash & hash;
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const formatCurrency = (value: number | undefined, currencyCode?: string) => {
    if (value === undefined || value === null) return '-';
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    const currency = currencyCode || '';

    return (
      <Text size="sm" c={isNegative ? 'red' : undefined} span>
        {isNegative && '('}
        {currency} <NumberFormatter
          value={absValue}
          thousandSeparator=","
          decimalScale={2}
        />
        {isNegative && ')'}
      </Text>
    );
  };

  const formatQuantity = (value: number | undefined) => {
    if (value === undefined || value === null) return '-';
    const isNegative = value < 0;
    const absValue = Math.abs(value);

    return (
      <Text size="sm" c={isNegative ? 'red' : undefined} span>
        {isNegative && '('}
        <NumberFormatter
          value={absValue}
          thousandSeparator=","
          decimalScale={2}
        />
        {isNegative && ')'}
      </Text>
    );
  };

  const hasPositions = positions.length > 0;

  // Find the highlighted position for quantity validation
  const highlightedPosition = useMemo(() => {
    if (!highlightInvestment || !highlightLocationAccount || !hasPositions) return null;
    return positions.find(position =>
      position.CODE === highlightInvestment &&
      position.CUSTODIANACCOUNT === highlightLocationAccount
    );
  }, [positions, highlightInvestment, highlightLocationAccount, hasPositions]);

  // Check if transaction quantity exceeds available position
  const quantityWarning = useMemo(() => {
    if (!enableQuantityWarning) return null;
    if (!highlightedPosition || !transactionQuantity || transactionQuantity <= 0) return null;

    const availableQuantity = highlightedPosition.QTYEND;
    const absTransactionQuantity = Math.abs(transactionQuantity);

    if (absTransactionQuantity > Math.abs(availableQuantity)) {
      return {
        available: availableQuantity,
        requested: transactionQuantity,
        excess: absTransactionQuantity - Math.abs(availableQuantity)
      };
    }
    return null;
  }, [highlightedPosition, transactionQuantity, enableQuantityWarning]);

  return (
    <Paper withBorder p="md">
      <Stack gap="md">
        {showTitle && (
          <Group justify="space-between">
            <Title order={4}>Portfolio Positions</Title>
            <Group gap="xs">
              <Badge variant="light" color="blue">
                {portfolioCode}
              </Badge>
              {date && (
                <Badge variant="outline" size="sm">
                  {new Date(date).toLocaleDateString()}
                </Badge>
              )}
              <ActionIcon
                variant="subtle"
                onClick={handleRefresh}
                loading={loading}
                disabled={loading}
              >
                <IconRefresh size={16} />
              </ActionIcon>
            </Group>
          </Group>
        )}

        {/* Quantity Warning */}
        {quantityWarning && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Insufficient Position Quantity"
            color="red"
            variant="light"
          >
            <Stack gap="xs">
              <Text size="sm">
                <strong>Warning:</strong> Transaction quantity exceeds available position!
              </Text>
              <Group gap="md">
                <Text size="sm">Available: <strong>{formatQuantity(quantityWarning.available)}</strong></Text>
                <Text size="sm">Requested: <strong>{formatQuantity(quantityWarning.requested)}</strong></Text>
                <Text size="sm" c="red">Excess: <strong>{formatQuantity(quantityWarning.excess)}</strong></Text>
              </Group>
            </Stack>
          </Alert>
        )}

        {loading && !hasPositions && (
          <Group justify="center" py="xl">
            <Loader size="md" />
            <Text c="dimmed">Loading portfolio positions...</Text>
          </Group>
        )}

        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        {!loading && !error && positions.length === 0 && (
          <Alert
            color="gray"
            variant="light"
            title="No Positions"
          >
            No positions found for this portfolio.
          </Alert>
        )}

        {hasPositions && (
          <Table.ScrollContainer minWidth={800}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Investment Code</Table.Th>
                  <Table.Th>Description</Table.Th>
                  {/* <Table.Th>Country</Table.Th> */}
                  <Table.Th style={{ textAlign: 'right' }}>Quantity</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Market Value (Local)</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Market Value (Book)</Table.Th>
                  <Table.Th>Custodian Account</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {positions.map((position, index) => {
                  const investmentTypeCode = position._INVESTMENT_INVESTMENTTYPE_CODE_;
                  const typeColor = getInvestmentTypeColor(investmentTypeCode);
                  const currencyCode = position._DENOMINATION_CODE_ || 'KWD';
                  const custodianColor = getInvestmentTypeColor(position.CUSTODIANACCOUNT);

                  // Check if this row matches the current form values
                  const isHighlighted = highlightInvestment && highlightLocationAccount &&
                    position.CODE === highlightInvestment &&
                    position.CUSTODIANACCOUNT === highlightLocationAccount;

                  return (
                    <Table.Tr
                      key={position._KEY_ || index}
                      onClick={() => onRowClick && onRowClick(position.CODE, position.CUSTODIANACCOUNT)}
                      style={{
                        cursor: onRowClick ? 'pointer' : 'default',
                        backgroundColor: isHighlighted ? 'var(--mantine-color-blue-0)' : undefined,
                        borderLeft: isHighlighted ? '4px solid var(--mantine-color-blue-6)' : undefined,
                      }}
                    >
                      <Table.Td>
                        <Text
                          size="xs"
                          c={typeColor}
                          fw={500}
                        >
                          {investmentTypeCode || 'N/A'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={500} c="purple">
                          {position.CODE || '-'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {position._INVESTMENT_DESCRIPTION_ || '-'}
                        </Text>
                      </Table.Td>
                      {/* <Table.Td>
                        <Group gap="xs">
                          {position.ISSUE_COUNTRY_CODE && (
                            <Badge size="xs" variant="outline">
                              {position.ISSUE_COUNTRY_CODE}
                            </Badge>
                          )}
                          <Text size="xs" c="dimmed">
                            {position.ISSUE_COUNTRY_DESCRIPTION || '-'}
                          </Text>
                        </Group>
                      </Table.Td> */}
                      <Table.Td style={{ textAlign: 'right' }}>
                        {formatQuantity(position.QTYEND)}
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        {formatCurrency(position.MARKETVALUELOCALEND, currencyCode)}
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        {formatCurrency(position.MARKETVALUEBOOKEND, currencyCode)}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c={custodianColor}>
                          {position.CUSTODIANACCOUNT || '-'}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
    </Paper>
  );
};